import React, { useEffect, useState } from "react";
import customFetch from "../../../helpers/axios";
import { normalizeApiResponse } from "../../../helpers/apiUtils";
import { Search, ChevronLeft, ArrowUpDown } from "lucide-react";
import Combobox from "../../components/Combobox/Combobox";
import SearchInput from "../../components/SearchInput/SearchInput";
import "./AllUsersTable.css";
import Button from "../../components/Buttons/Button/Button";
import Pagination from "../../components/Pagination/Pagination";
import "../../components/Pagination/Pagination.css";
import { useTranslation } from "react-i18next";
import useFormatDate from "../../../hooks/useFormatDate";
import AllUsersUsageModal from "./AllUsersUsageModal";

const ITEMS_PER_PAGE = 10;

export default function AllUsersTable({ onBack }) {
  const { t } = useTranslation('usage');
  const formatDate = useFormatDate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("usage_date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [dateFilter, setDateFilter] = useState("all");
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null
  });

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        // Build ordering with a secondary key when sorting by agent to stabilize grouping
        const orderingKey =
          sortColumn === 'llm_model__name'
            ? `${sortDirection === 'desc' ? '-' : ''}llm_model__name,${sortDirection === 'desc' ? '-' : ''}usage_date`
            : `${sortDirection === 'desc' ? '-' : ''}${sortColumn}`;

        const params = new URLSearchParams({
          page: page.toString(),
          ordering: orderingKey,
          date_filter: dateFilter,
          search: searchTerm
        });
        // No agent filter here; ordering handles grouping by agent
        
        const response = await customFetch.get(`/api/logic_layer/usage/?${params}`);
        const normalizedResponse = normalizeApiResponse(response.data);
        setUsers(normalizedResponse.results);
        setPagination({
          count: normalizedResponse.count,
          next: normalizedResponse.next,
          previous: normalizedResponse.previous
        });
      } catch (err) {
        setError(err);
        setUsers([]);
        setPagination({ count: 0, next: null, previous: null });
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [page, sortColumn, sortDirection, dateFilter, searchTerm]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortableHeader = (column, label, align = 'left') => (
    <th 
      className={`usage-sortable-th ${align === 'right' ? 'align-right' : ''}`}
      onClick={() => handleSort(column)}
    >
      <div className={`usage-sortable-header ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        <ArrowUpDown 
          size={14}
          className={`usage-sort-icon ${sortColumn === column ? 'active' : ''} ${sortColumn === column && sortDirection === 'desc' ? 'desc' : ''}`}
        />
      </div>
    </th>
  );

  const totalPages = Math.ceil(pagination.count / ITEMS_PER_PAGE);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = (rec) => { setSelectedRecord(rec); setIsModalOpen(true); };
  const closeModal = () => { setIsModalOpen(false); setSelectedRecord(null); };

  return (
    <div className="usage-all-users-root">
      <div className="user-mgmt-table-section">
        {/* Header with search and filters */}
        <div className="user-mgmt-table-header">
        <div className="text-2xl font-semibold leading-none tracking-tight">
          {t('allUsers.title')}
        </div>

        <div className="user-mgmt-searchbar">
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('allUsers.searchPlaceholder')}
            className="max-w-md"
          />

          <Combobox
            value={dateFilter}
            onChange={setDateFilter}
            options={[
              { value: "all", label: t('allUsers.filters.all') },
              { value: "today", label: t('allUsers.filters.today') },
              { value: "week", label: t('allUsers.filters.week') },
              { value: "month", label: t('allUsers.filters.month') },
              { value: "year", label: t('allUsers.filters.year') },
            ]}
            buttonClass="user-mgmt-filter-select"
          />

          <Button
            variant="primary"
            size="md"
            style={{
            height: '2.25rem',
            width: 'fit-content',
            }}
            onClick={onBack}
            leftIcon={<ChevronLeft size={16} />}
          >
            {t('allUsers.back')}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="usage-loading">
          <p className="usage-pagination-info">{t('allUsers.loading')}</p>
        </div>
      )}
      
      {error && (
        <div className="usage-error">
          <p>{t('allUsers.errorPrefix')} {error.message}</p>
        </div>
      )}
      
      {!loading && !error && (
        <div className="usage-users-table-wrapper usage-users-table-card">
          <table className="usage-users-table">
            <thead>
              <tr className="usage-users-thead-row">
                <th className="usage-th-index">#</th>
                {renderSortableHeader('user__name', t('allUsers.table.name'))}
                {renderSortableHeader('user__email', t('allUsers.table.email'))}
                {renderSortableHeader('credits', t('allUsers.table.credits'), 'right')}
                {renderSortableHeader('final_responses', t('allUsers.table.messages'), 'right')}
                {renderSortableHeader('iterations', t('allUsers.table.iterations'), 'right')}
                {renderSortableHeader('tool_calls', t('allUsers.table.toolCalls'), 'right')}
                {renderSortableHeader('prompts', t('allUsers.table.prompts'), 'right')}
                {renderSortableHeader('usage_date', t('allUsers.table.usageDate'))}
                {renderSortableHeader('llm_model__name', t('allUsers.table.agent'))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <tr 
                  key={u.id}
                  className="usage-row"
                  style={{ cursor: 'pointer' }}
                  onClick={() => openModal(u)}
                >
                  <td className="usage-td">{((page - 1) * ITEMS_PER_PAGE) + idx + 1}</td>
                  <td className="usage-td usage-td-strong">{u.user?.name || '-'}</td>
                  <td className="usage-td usage-td-muted">{u.user?.email || '-'}</td>
                  <td className="usage-td usage-td-right usage-td-strong">{u.credits ? (Math.round(u.credits * 10) / 10).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                  <td className="usage-td usage-td-right">{u.final_responses || '-'}</td>
                  <td className="usage-td usage-td-right">{u.iterations || '-'}</td>
                  <td className="usage-td usage-td-right">{u.tool_calls || '-'}</td>
                  <td className="usage-td usage-td-right">{u.prompts || '-'}</td>
                  <td className="usage-td">{u.usage_date ? formatDate(u.usage_date) : '-'}</td>
                  <td className="usage-td usage-td-strong">{u.llm_model_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination: shown below the table card */}
      {!loading && !error && pagination.count > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={(next) => setPage(next)}
          infoBelow={
            <span className="usage-page-info">
              {t('allUsers.pagination.showing')} {((page - 1) * ITEMS_PER_PAGE) + 1} {t('allUsers.pagination.to')} {Math.min(page * ITEMS_PER_PAGE, pagination.count)} {t('allUsers.pagination.of')} {pagination.count} {t('allUsers.pagination.entries')}
            </span>
          }
        />
      )}

      {!loading && !error && users.length === 0 && (
        <div className="text-center py-8">
          <Search size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('allUsers.emptyTitle')}
          </h3>
          <p className="text-gray-600">
            {t('allUsers.emptyDesc')}
          </p>
        </div>
      )}
      </div>

      {/* Details Modal */}
      <AllUsersUsageModal isOpen={isModalOpen} onClose={closeModal} record={selectedRecord} />
    </div>
  );
}
